import {
  assertReadFrameColumnNamesUsable,
  readFrameColumnOutputSocket,
  readFrameReservedOutputSockets,
} from "../../steps/data-access/stateful-table-read-frame.step";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { prettify } from "./socketDefinition";
import { statefulTableColumnTypeToSocketType } from "./statefulTableSocketTypes";

/** A column entry from a step's design-time schema snapshot. */
type ColumnSnapshot = { name: string; type: string };

/**
 * Normalize a step config's `columns` snapshot. It is persisted workflow config,
 * so a malformed entry degrades to being skipped rather than crashing socket
 * generation.
 */
function usableColumns(config: Record<string, unknown>): ColumnSnapshot[] {
  const columns = Array.isArray(config.columns) ? config.columns : [];
  return columns.filter(
    (c): c is ColumnSnapshot =>
      typeof c === "object" &&
      c !== null &&
      typeof (c as { name?: unknown }).name === "string" &&
      (c as { name: string }).name.length > 0 &&
      typeof (c as { type?: unknown }).type === "string",
  );
}

/**
 * Generate filter input sockets for stateful_table_read_frame. One optional
 * socket per column from the schema snapshot. Wiring a value filters the
 * frame to rows where that column equals the value; unwired sockets are
 * ignored. Multiple wired filters are AND-joined.
 */
export function generateStatefulTableReadFrameInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return buildDynamicSockets({
    version: effectiveSocketIdVersion("stateful_table_read_frame", config),
    entries: usableColumns(config).map((c) => ({
      name: c.name,
      socket: {
        name: prettify(c.name),
        type: statefulTableColumnTypeToSocketType(c.type),
        required: false,
        description: `Optional exact-match filter on column "${c.name}"`,
      },
    })),
  }).sockets;
}

/**
 * Generate output sockets for stateful_table_read_frame: the six reserved
 * whole-frame sockets, plus ONE array socket per column from the schema
 * snapshot in config.
 *
 * The per-column arrays exist so the engine's lockstep fanout can zip them into
 * a downstream per-row step's scalar sockets (the `core_enum_fanout` pattern):
 * `rows` is positional (`array<json>`), which no scalar socket accepts and no
 * expression can index, so before these sockets there was no way to wire a
 * frame into a per-row step at all.
 *
 * Element types come from the same `statefulTableColumnTypeToSocketType` map the
 * filter inputs and `stateful_table_lookup_row` use, so one column is typed
 * identically everywhere — just wrapped in `array<...>` here.
 *
 * At version 1 a column name that would collide with a reserved socket id or
 * with another column throws (see `assertReadFrameColumnNamesUsable`) — the
 * editor turns that into a toast, because the snapshot comes from the selected
 * table and a table may legitimately have a column named `rows`. Silently
 * shadowing a socket would send the wrong data downstream.
 *
 * At version 2 the marker makes the reserved collision impossible, so only a
 * duplicate column name is still a problem — and a table cannot have two
 * columns of the same name, so in practice the check has nothing left to catch.
 */
export function generateStatefulTableReadFrameOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const usable = usableColumns(config);
  const version = effectiveSocketIdVersion("stateful_table_read_frame", config);

  if (version < 2) {
    assertReadFrameColumnNamesUsable(usable.map((c) => c.name));
  }

  const reserved = readFrameReservedOutputSockets();
  const { sockets: columnSockets } = buildDynamicSockets({
    reserved: reserved.map((s) => s.id),
    version,
    entries: usable.map((c) => {
      const socket = readFrameColumnOutputSocket({
        columnName: c.name,
        cellSocketType: statefulTableColumnTypeToSocketType(c.type),
        displayName: prettify(c.name),
      });
      // `readFrameColumnOutputSocket` owns the socket's shape (its array
      // wrapping, its description); only the id is ours to derive.
      const { id: _derivedId, ...body } = socket;
      return { name: c.name, socket: body };
    }),
  });

  return [...reserved, ...columnSockets];
}

/**
 * Generate output sockets for stateful_table_lookup_row. One socket per
 * column from the schema snapshot in config — the handler returns the
 * looked-up row's cell values keyed by column name.
 */
export function generateStatefulTableLookupRowOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return buildDynamicSockets({
    version: effectiveSocketIdVersion("stateful_table_lookup_row", config),
    entries: usableColumns(config).map((c) => ({
      name: c.name,
      socket: {
        name: prettify(c.name),
        type: statefulTableColumnTypeToSocketType(c.type),
        required: false,
        description: `Value of column "${c.name}" from the matched row`,
      },
    })),
  }).sockets;
}
